import Animated, {
  type SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import {
  horizontalDragFreeDistance,
  horizontalSwipePressProgress,
} from "@-/dev.improve.haniverse.lib.learn.deck";
import type { LearnDeckStageValues } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";

/**
 * Presses a stationary button while a card is dragged toward the action it commits: faint
 * through the free range, fully pressed past the release threshold. It never moves the button
 * or dims its base, and fades out as soon as the drag ends.
 *
 * `pressed` (1 while a finger is down on the button itself, set on the UI thread) shows the same
 * full press at once, so a press and a swipe toward the button look alike.
 */
export function LearnFlashcardSwipePressHighlight({
  className,
  direction,
  pressed,
  stage: { cardHeight, cardWidth, dragX, isDragging },
  testID,
}: {
  className?: string;
  /** `-1` presses on a left drag, `1` on a right drag, `"both"` on either; omitted, never. */
  direction?: -1 | 1 | "both";
  pressed?: SharedValue<number>;
  stage: Pick<LearnDeckStageValues, "cardHeight" | "cardWidth" | "dragX" | "isDragging">;
  testID?: string;
}) {
  // Recompute only when settled dimensions change, never by measuring the moving card.
  const threshold = useDerivedValue(() =>
    horizontalDragFreeDistance(cardWidth.get(), cardHeight.get()),
  );
  const animatedStyle = useAnimatedStyle(() => {
    const swipe =
      direction !== undefined && isDragging.get()
        ? horizontalSwipePressProgress(
            direction === "both" ? Math.abs(dragX.get()) : dragX.get() * direction,
            threshold.get(),
          )
        : 0;
    // A finger on the button shows the press in the same frame; only the release fades.
    return pressed !== undefined && pressed.get() > 0
      ? { opacity: 1 }
      : { opacity: withTiming(swipe, { duration: 120 }) };
  });
  return (
    <Animated.View
      className={
        className === undefined
          ? "absolute inset-0 bg-black/10"
          : `absolute inset-0 bg-black/10 ${className}`
      }
      pointerEvents="none"
      style={animatedStyle}
      testID={testID}
    />
  );
}
